import { useCallback } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { ensureOnboardingOverviewData } from "@/api/onboarding/onboardingQueries";
import { getModuleConfig } from "@/components/onboarding/configs/getModuleConfig";
import { useMap } from "@/hooks/map/core/useMapContext";
import { AppDispatch, RootState } from "@/store";
import { buildAnalysisConfigSnapshot } from "@/store/analysisConfigSnapshot";
import {
  clearAnalysisConfigSnapshot,
  parkAnalysisConfig,
  selectHasParkedAnalysisConfig,
  selectParkedAnalysisConfig
} from "@/store/slice/analysisConfigSnapshotSlice";
import {
  enterOnboarding,
  exitOnboarding,
  selectOnboardingSession
} from "@/store/slice/onboardingSlice";

import {
  captureMapCamera,
  getSnapshotDrawnPolygonFeatures,
  resetOnboardingMapLayers,
  resetOnboardingSlices,
  resolveModuleId,
  restoreDrawnPolygons,
  restoreMapCamera,
  restoreOnboardingMapLayers,
  restoreOnboardingSlices
} from "./utils";

export function useEnterOnboardingMode() {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    map,
    deleteAllFeatures,
    resetAllFeatureStates,
    handleRemoveSourceLayer
  } = useMap();

  return useCallback(
    async (moduleCode: string) => {
      const config = getModuleConfig(moduleCode);

      if (!config) {
        console.warn(
          `[useEnterOnboardingMode] no module config for code "${moduleCode}"`
        );
        return;
      }

      // Do not start if a different mode holds the shared workspace. The entry
      // navigates, resets Redux and removes map layers. The parked snapshot is
      // the only copy of them. Read the live state, not a selector value: this
      // callback runs from click and async dialog handlers.
      if (selectHasParkedAnalysisConfig(store.getState())) {
        console.warn(
          "[useEnterOnboardingMode] a workspace session is already active"
        );
        return;
      }

      // The steps of a tour point to DOM elements of one page only
      // (config.route, default "/"). Go to that page first, thus each step can
      // find its target. Keep the initial path, thus the exit can go back to it.
      // The target page mounts while the cache below loads.
      const entryPath = location.pathname;
      const tourRoute = config.route ?? "/";

      if (entryPath !== tourRoute) navigate(tourRoute);

      // The login flow fills this cache. ensureQueryData stays here for a direct
      // entry or for a background preload that failed.
      const [modulesResp] = await ensureOnboardingOverviewData(queryClient);

      const moduleId = resolveModuleId(modulesResp, moduleCode);

      if (!moduleId) {
        console.warn(
          `[useEnterOnboardingMode] module code "${moduleCode}" not in BE module list`
        );
        return;
      }

      // Save the state before the reset, thus exitOnboardingMode can put it back.
      // The camera is on the map instance and not in Redux. Thus this function
      // also saves the camera.
      //
      // Use store.getState() and not useSelector. This code runs after an async
      // preload, thus it must read the current state and not a selector value
      // from a previous render. A direct read also keeps the hook independent of
      // the dataset slice, which changes frequently.
      const state = store.getState();

      // Examine the workspace again and claim it in the same synchronous block.
      // The first check ran before the await, thus a different mode can hold the
      // workspace now. Do not put an await between this line and the reset below.
      // If you lose the race, the reset removes the slices and the map layers,
      // and no snapshot of the workspace stays.
      if (selectHasParkedAnalysisConfig(state)) {
        console.warn(
          "[useEnterOnboardingMode] lost the workspace to another session"
        );
        return;
      }

      dispatch(parkAnalysisConfig(buildAnalysisConfigSnapshot(state)));
      dispatch(
        enterOnboarding({
          moduleCode,
          parkedUi: state.ui,
          parkedMapCamera: captureMapCamera(map),
          entryPath
        })
      );

      toast.dismiss();

      resetOnboardingSlices(dispatch);
      // Clear the map view. The polygons, the highlights and the camera position
      // stay after a Redux reset. Remove them for a clean onboarding map.
      deleteAllFeatures();
      resetAllFeatureStates();
      // The dataset sources and layers also stay after a Redux reset.
      // Remove them, and read the datasets from the state before the reset.
      resetOnboardingMapLayers(state.dataset.datasets, handleRemoveSourceLayer);
    },
    [
      dispatch,
      store,
      queryClient,
      navigate,
      location.pathname,
      map,
      deleteAllFeatures,
      resetAllFeatureStates,
      handleRemoveSourceLayer
    ]
  );
}

export function useExitOnboardingMode() {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    map,
    deleteAllFeatures,
    renderDrawPolygon,
    handleRemoveSourceLayer,
    handleResetLayerData,
    handleResetLayerOrder
  } = useMap();

  const session = useSelector(selectOnboardingSession);
  const snapshot = useSelector(selectParkedAnalysisConfig);

  return useCallback(() => {
    // Only the owner can exit. Thus this callback does not clear a workspace of
    // Agentic AI. A route effect after the end of the tour has no session.
    if (!session) return;

    if (snapshot) {
      const currentDatasets = store.getState().dataset.datasets;

      // Put the saved slice state back into Redux. enterOnboardingMode reset and
      // seeded each parked slice. Without this step the real data of the user
      // does not come back.
      restoreOnboardingSlices(dispatch, snapshot, session.parkedUi);
      // Move the live map back to the view of the onboarding entry.
      restoreMapCamera(map, session.parkedMapCamera);
      // The entry removed the dataset sources and layers with
      // resetOnboardingMapLayers. restoreOnboardingSlices puts only the dataset
      // slice back in Redux. Thus draw the MapLibre sources and layers again from
      // the snapshot and set their order.
      restoreOnboardingMapLayers(
        snapshot.dataset,
        currentDatasets,
        handleRemoveSourceLayer,
        handleResetLayerData,
        handleResetLayerOrder
      );
      // The entry cleared the draw control. restoreOnboardingSlices puts only the
      // polygon state back in Redux. Draw from the snapshot, thus the saved, the
      // draft and the custom polygons come back.
      restoreDrawnPolygons(
        deleteAllFeatures,
        renderDrawPolygon,
        getSnapshotDrawnPolygonFeatures(snapshot)
      );
    }

    // `useTourStep` clears `activeStepTourId` when the tour unmounts.
    dispatch(clearAnalysisConfigSnapshot());
    dispatch(exitOnboarding());

    // The entry moved the user to the route of the tour. Move the user back to
    // the initial path. Compare with the live path and not with "/", because some
    // tours do not run on the homepage. For example, Bring Your Own Data runs on
    // /dataset-management. If entryPath is null, this step does nothing.
    if (session.entryPath && session.entryPath !== location.pathname) {
      navigate(session.entryPath);
    }
  }, [
    dispatch,
    store,
    navigate,
    location.pathname,
    session,
    snapshot,
    map,
    deleteAllFeatures,
    renderDrawPolygon,
    handleRemoveSourceLayer,
    handleResetLayerData,
    handleResetLayerOrder
  ]);
}
